import { useIntl } from 'react-intl';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Moment } from 'moment';

import { API } from 'src/reactQuery/Api';
import { postRequest } from 'src/utils/request';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import messages from 'src/pages/TimeTrackingPage/messages';
import { RequestSimplifiedData } from 'src/types/requestData';
import { useMessageHandler } from 'src/utils/hooks/useMessageHandler';

export interface UserRecordType {
  adminComment: string;
  checkIn: Moment | string | null;
  checkOut: Moment | string | null;
  clientCheckin: Moment | null;
  clientCheckout: Moment | null;
  checkinDeviationReason: string | null;
  checkoutDeviationReason: string | null;
  billableHours: string;
  nightHours: string;
  sundayHours: string;
  holidayHours: string;
  wage: string;
  wageBonus: string;
  price: string;
  priceBonus: string;
  isBreakTaken: boolean;
  breakHours: string;
}

type UpdateApproveTimeTrackingParamsType = {
  record: UserRecordType;
  timeTrackingUuid: string;
};

const updateApproveTimeTracking = async ({
  record,
  timeTrackingUuid,
}: UpdateApproveTimeTrackingParamsType) => {
  const params = {
    candidate_job_shift: {
      wage: parseFloat(record.wage),
      price: parseFloat(record.price),
      wage_bonus: parseFloat(record.wageBonus),
      break_hour: parseFloat(record.breakHours),
      night_hour: parseFloat(record.nightHours),
      price_bonus: parseFloat(record.priceBonus),
      sunday_hour: parseFloat(record.sundayHours),
      holiday_hour: parseFloat(record.holidayHours),
      admin_comment: record.adminComment,
      billable_hour: parseFloat(record.billableHours),
      client_checkin_time: record.clientCheckin,
      client_checkout_time: record.clientCheckout,
      checkin_deviation_reason_code: record.checkinDeviationReason,
      checkout_deviation_reason_code: record.checkoutDeviationReason,
    },
  };

  const { data } = await postRequest<RequestSimplifiedData>(
    API.POST.approveTimeTracking(timeTrackingUuid),
    params,
  );

  return data;
};

export const useUpdateApproveTimeTracking = () => {
  const client = useQueryClient();
  const { formatMessage } = useIntl();
  const { handleError, handleSuccess } = useMessageHandler();

  return useMutation({
    mutationFn: updateApproveTimeTracking,
    onSuccess: () => {
      handleSuccess(formatMessage(messages.timeTrackingApproved), 1000);

      client.invalidateQueries({ queryKey: [QUERY_KEYS.CANDIDATE_JOB_SHIFTS] });
    },
    onError: handleError,
  });
};
